import { Box, CircularProgress, Step, StepButton, Stepper } from '@mui/material';
import {
  SETUP_LANGUAGE_TABS,
  GENERAL_SETUP_LANGUAGE_MAP,
  PROJECT_SETUP_LANGUAGE_MAP,
  GENERAL_SETUP_LANGUAGE_OPTIONS,
  PROJECT_SETUP_LANGUAGE_OPTIONS,
} from '@constants';
import { useMemo, useState } from 'react';
import { StyledStepLabel } from '@components/ExpandableStepper/ExpandableStepper.styled';
import { useFormContext, useWatch } from 'react-hook-form';
import { TranslationSetupForm } from '@customTypes';
import { useGetGeneralTranslationsQuery, useGetProjectTranslationDataQuery } from '@services/api/translation.api';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { setSetupLanguageChanges } from '@store/reducers/project.reducer';
import useCustomTranslation from '@hooks/useCustomTranslation';

import { TranslationSetup } from './TranslationSetup';
import styles from './TranslationSetupDialog.module.scss';

interface LanguageSetupTabProps {
  type: SETUP_LANGUAGE_TABS;
  confirmTabChange: () => void;
}

const TranslationSetupTab = ({ type, confirmTabChange }: LanguageSetupTabProps) => {
  const { getTranslation } = useCustomTranslation();
  const { control, setValue } = useFormContext<TranslationSetupForm>();
  const [projectAnalysisActiveTab, setProjectAnalysisActiveTab] = useState(0);
  const {
    selectedProject,
    setupLanguageDialog: { changedValues },
  } = useAppSelector((state) => state.projects);
  const dispatch = useAppDispatch();
  const language = useWatch({
    control,
    name: 'language',
  });

  const { data: generalData, isFetching: isFetchingGeneralData } = useGetGeneralTranslationsQuery(
    {
      languageId: language,
      languagePool: GENERAL_SETUP_LANGUAGE_OPTIONS[projectAnalysisActiveTab],
    },
    {
      skip: type === SETUP_LANGUAGE_TABS.PROJECT || !language,
      refetchOnReconnect: true,
    },
  );

  const { data: projectData, isFetching: isFetchingProjectData } = useGetProjectTranslationDataQuery(
    {
      languageId: language,
      businessId: selectedProject?.selectedBusinessId,
      languagePool: PROJECT_SETUP_LANGUAGE_OPTIONS[projectAnalysisActiveTab],
    },
    {
      skip: type === SETUP_LANGUAGE_TABS.GENERAL || !language,
      refetchOnReconnect: true,
    },
  );

  const handleChangeTab = (activeStep: number) => {
    if (Object.keys(changedValues).length) {
      confirmTabChange();
    } else {
      setValue('projectAnalysisActiveTab', activeStep);
      setProjectAnalysisActiveTab(activeStep);
      dispatch(setSetupLanguageChanges({}));
    }
  };

  const steps = useMemo(() => {
    const step = type === SETUP_LANGUAGE_TABS.GENERAL ? GENERAL_SETUP_LANGUAGE_MAP : PROJECT_SETUP_LANGUAGE_MAP;

    return step.map((option) => ({ value: option.value, label: getTranslation(option.key, option.i18nKey) }));
  }, [type]);

  return (
    <div className={styles.container}>
      <Box className={styles.toolsWrapper}>
        <Box className={styles.stepperWrapper}>
          <Stepper activeStep={projectAnalysisActiveTab} orientation="vertical">
            <Step>
              {steps.map((step, index) => (
                <StepButton disableRipple key={index} onClick={() => handleChangeTab(Number(step.value))}>
                  <StyledStepLabel active={projectAnalysisActiveTab === Number(step.value)}>
                    {step.label}
                  </StyledStepLabel>
                </StepButton>
              ))}
            </Step>
          </Stepper>
        </Box>
      </Box>
      {(isFetchingGeneralData || isFetchingProjectData) && <CircularProgress />}
      {!isFetchingGeneralData && !isFetchingProjectData && type === SETUP_LANGUAGE_TABS.GENERAL && (
        <TranslationSetup data={language ? generalData?.payload : undefined} activeTab={projectAnalysisActiveTab} />
      )}
      {!isFetchingGeneralData && !isFetchingProjectData && type === SETUP_LANGUAGE_TABS.PROJECT && (
        <TranslationSetup data={language ? projectData?.payload : undefined} activeTab={projectAnalysisActiveTab} />
      )}
    </div>
  );
};

export default TranslationSetupTab;
